import { z } from 'zod';
import { currencySchema, kpiSourceSchema, pillarSchema, unitSchema, valueTypeSchema } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { periodSchema, resolvedPeriodSchema } from '../../common/schemas/period.schemas.js';
import { trackerTargetSchema } from '../../tracker/schemas/tracker.schemas.js';
import { weekSchema } from '../../weeks/schemas/weeks.schemas.js';
/** More than the whole catalogue (~55 KPIs), with room to grow. */
export const REPORT_MAX_KPIS = 500;
export const steeringReportInputSchema = z.object({
    period: periodSchema,
    /** Narrows the report to one pillar; a pillar the caller cannot see yields an empty report, never an error. */
    pillar: pillarSchema.optional(),
    /** The KPIs to report on; omitted = every KPI of the (visible, filtered) pillars. Ids the caller cannot see are ignored. */
    kpiIds: z.array(uuidSchema).max(REPORT_MAX_KPIS, `Select at most ${REPORT_MAX_KPIS} KPIs`).optional(),
    includeInactive: z.boolean().default(false),
});
/** A KPI as the report shows it. Never carries the formula, its preview or its source ids. */
export const reportKpiSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    pillar: pillarSchema,
    name: z.string(),
    unit: unitSchema,
    currency: currencySchema.nullable(),
    valueType: valueTypeSchema,
    source: kpiSourceSchema,
    active: z.boolean(),
    /** A calculated KPI whose sources reach into a pillar the caller cannot see: every figure is blank. */
    restricted: z.boolean(),
    parentKpiId: uuidSchema.nullable(),
    /** 0 = top level; parents come before their sub-KPIs. */
    depth: z.number().int().min(0),
});
/** A weekly value: a number, or the text of a Text KPI. A blank is absent (or `null`), never 0. */
export const reportValueSchema = z.union([z.number(), z.string()]);
export const reportWeekValueSchema = z.object({
    weekId: uuidSchema,
    value: reportValueSchema.nullable(),
});
export const movementDirectionSchema = z.enum(['UP', 'DOWN', 'FLAT']);
export const completionBasisSchema = z.enum(['TOTAL', 'AVERAGE', 'LATEST']);
/** One row per KPI: the latest-week summary and the range summary, in the order of `kpis`. */
export const steeringRowSchema = z.object({
    kpiId: uuidSchema,
    /** The most recent week of the range with a value, and that value; `null` when the range has none. */
    latest: reportWeekValueSchema.nullable(),
    /** The reporting week right before `latest` (it may precede the range) and its value, which may be blank. */
    previous: reportWeekValueSchema.nullable(),
    /** `latest − previous`, only when both are numbers. */
    change: z.number().nullable(),
    /** `change ÷ |previous| × 100`, only when the previous value is a real non-zero number. */
    changePct: z.number().nullable(),
    /** Neutral movement (`↑ ↓ —`); `null` when there is nothing to compare. */
    movement: movementDirectionSchema.nullable(),
    /** Sum / mean / most recent of the numeric weekly values of the range; `null` (never 0) without any. */
    total: z.number().nullable(),
    average: z.number().nullable(),
    rangeLatest: z.number().nullable(),
    /** The active target covering the To week, if any. */
    target: trackerTargetSchema.nullable(),
    /** Which range figure the target is compared with (`completionBasis` in the engine); `null` without a target. */
    completionBasis: completionBasisSchema.nullable(),
    /** Completion in percent; `null` without a target or without an actual to compare. */
    completion: z.number().nullable(),
});
/** One trend chart: KPIs sharing a unit and (for Currency) a currency — a chart never mixes two. */
export const seriesGroupSchema = z.object({
    /** `${unit}|${currency}` (currency empty when not Currency). */
    key: z.string(),
    unit: unitSchema,
    currency: currencySchema.nullable(),
    series: z.array(z.object({
        kpiId: uuidSchema,
        /** One point per week of `weeks`; `null` is a gap, never drawn as 0. */
        points: z.array(z.number().nullable()),
    })),
});
export const steeringReportOutputSchema = z.object({
    period: resolvedPeriodSchema,
    /** The resolved weeks, oldest first. */
    weeks: z.array(weekSchema),
    /** The week before the range, when the first week's movement needs it. */
    previousWeek: weekSchema.nullable(),
    /** The reported KPIs: pillar order, parents before sub-KPIs, then display order. */
    kpis: z.array(reportKpiSchema),
    rows: z.array(steeringRowSchema),
    /** Numeric, non-restricted KPIs grouped by unit + currency, in first-appearance order. */
    groups: z.array(seriesGroupSchema),
    /** The data table: `${kpiId}:${weekId}` → value for the weeks of the range; a missing key is a blank. */
    values: z.record(z.string(), reportValueSchema),
    /** The week of the Sunday on or before today in Asia/Amman; `null` when it was never generated. */
    currentWeekId: uuidSchema.nullable(),
});
//# sourceMappingURL=reports.schemas.js.map